import React, { useEffect, useState } from "react";
import "./UserTable.scss";
import DeleteConfirmation from "../DeleteConfirmation/DeleteConfirmation";
// import Loading from "../Loading/Loading";
import EditUserForm from "../EditUserForm/EditUserForm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { fetchDeactivateUser, fetchUpdateUser, updateUserInList } from "../../Reducers/actions/userActions";
import { useToast } from "../../hooks/useToast";
import { fetchPostApi } from "../../../helpers/fetch";
import { getUserDisplayName } from "../../../helpers/userDisplayName";
import Icon from "../Icon";
import Modal from "../Modal/Modal";
// import { useSelector } from "react-redux";

// const initialUsers = Array.from({ length: 20 }, (_, index) => ({
//     id: index + 1,
//     name: `User ${index + 1}`,
//     email: `user${index + 1}@example.com`,
//     joinDate: `2023-${String((index % 12) + 1).padStart(2, "0")}-${String((index % 28) + 1).padStart(2, "0")}`,
//     isCreator: index % 2 === 0, 
// }));

const UserTable = () => {
    const { users: init_users } = useSelector(state => ({
        users: Array.isArray(state.user.users) ? state.user.users : (state.user.users?.users || [])
    }));

    const dispatch = useDispatch()
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [editUser, setEditUser] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [userTypeFilter, setUserTypeFilter] = useState('all'); // 'all', 'users', 'creators'
    const [roleStatusFilter, setRoleStatusFilter] = useState('activity'); // 'admin', 'activity'
    const [showEditTableModal, setShowEditTableModal] = useState(false);
    const [usersPerPage, setUsersPerPage] = useState(10);
    const [columnVisibility, setColumnVisibility] = useState({
        username: true,
        admin: true,
        email: true,
        invitedBy: true,
        role: true,
        status: true,
        createdAt: true,
        emailVerified: true,
        profileCompleted: true,
        firstPublication: true,
        firstContribution: true,
        earnings: false,
        spendings: false,
        subscribers: true,
        contributors: false,
        contributions: false,
        invitesUsers: false
    });
    const toast = useToast()

    useEffect(() => {
        if (init_users && init_users.length > 0) {
            setUsers(init_users);
        }
    }, [init_users])

    // Check if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const handleUserTypeFilter = (type) => {
        setUserTypeFilter(type);
        setCurrentPage(1);
    };

    const handleRoleStatusFilter = (filter) => {
        setRoleStatusFilter(filter);
        setCurrentPage(1);
    };

    // Helper function to check if user is a creator
    const isCreator = (user) => {
        return user.role === 'creator' || (user.creations_count && user.creations_count > 0);
    };

    // Helper function to check if user meets eligibility criteria
    // Uses is_early_adopter_eligible from API if available, otherwise calculates it
    const isEligible = (user) => {
        if (user.is_early_adopter_eligible !== undefined) {
            return user.is_early_adopter_eligible;
        }
        // Fallback calculation if API doesn't provide the field
        const creationsCount = user.creations_count || 0;
        const acceptedInvitesCount = user.accepted_invites_count || 0;
        return creationsCount >= 5 && acceptedInvitesCount >= 5;
    };

    const isProfileComplete = (user) => {
        return !!(
            user.firstName &&
            user.lastName &&
            user.userName &&
            user.profilePicture &&
            user.bio &&
            (user.instagram || user.twitter || user.facebook || user.youtube)
        );
    };

    const hasFirstPublication = (user) => {
        return !!(user.first_post_date || user.creations_count > 0);
    };

    // Matches marketing email gate: email confirmed AND at least one sign-in
    // (bulk-imported accounts can have email_confirmed_at without ever logging in).
    const isEmailVerified = (user) => {
        return !!(user.email_confirmed_at && user.last_sign_in_at);
    };

    const getEmailVerifiedTooltip = (user) => {
        if (isEmailVerified(user)) {
            return `Verified account — last sign-in: ${moment(user.last_sign_in_at).format('YYYY-MM-DD')}`;
        }
        if (user.email_confirmed_at) {
            return 'Email confirmed but user has never signed in';
        }
        return 'Email not verified';
    };

    const hasFirstContribution = (user) => {
        return !!user.first_contribution_date;
    };

    const filteredUsers = users.filter(user => {
        // Apply user type filter
        if (userTypeFilter === 'users') {
            if (isCreator(user)) return false;
        } else if (userTypeFilter === 'creators') {
            if (!isCreator(user)) return false;
        }

        // Apply role/status filter
        if (roleStatusFilter === 'admin') {
            // Admin filter shows only admin users
            if (user.role !== 'admin') return false;
        } else if (roleStatusFilter === 'activity') {
            // Activity filter shows active users
            if (user.accountStatus?.toLowerCase() !== 'active') return false;
        }

        // Apply search query
        const searchLower = searchQuery.toLowerCase();
        if (searchQuery) {
            const matchesSearch = (
                user.userName?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.role?.toLowerCase().includes(searchLower) ||
                user.accountStatus?.toLowerCase().includes(searchLower)
            );
            if (!matchesSearch) return false;
        }

        return true;
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        if (!sortConfig.key) return 0;

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle computed boolean columns
        if (sortConfig.key === 'profile_completed') {
            aValue = isProfileComplete(a) ? 1 : 0;
            bValue = isProfileComplete(b) ? 1 : 0;
        } else if (sortConfig.key === 'has_first_publication') {
            aValue = hasFirstPublication(a) ? 1 : 0;
            bValue = hasFirstPublication(b) ? 1 : 0;
        } else if (sortConfig.key === 'has_first_contribution') {
            aValue = hasFirstContribution(a) ? 1 : 0;
            bValue = hasFirstContribution(b) ? 1 : 0;
        } else if (sortConfig.key === 'email_verified') {
            aValue = isEmailVerified(a) ? 1 : 0;
            bValue = isEmailVerified(b) ? 1 : 0;
        }

        const dateColumns = ['created_at', 'first_post_date', 'first_contribution_date'];
        if (dateColumns.includes(sortConfig.key)) {
            aValue = aValue ? new Date(aValue) : new Date(0);
            bValue = bValue ? new Date(bValue) : new Date(0);
        }

        // Handle numeric columns
        const numericColumns = [
            'creations_count',
            'accepted_invites_count',
            'earnings',
            'spendings',
            'subscribers',
            'contributors',
            'contributions',
            'invites_users'
        ];
        if (numericColumns.includes(sortConfig.key)) {
            aValue = aValue !== null && aValue !== undefined ? Number(aValue) : 0;
            bValue = bValue !== null && bValue !== undefined ? Number(bValue) : 0;
        } else if (typeof aValue === 'string' && aValue !== null) {
            aValue = aValue.toLowerCase();
        }
        if (typeof bValue === 'string' && bValue !== null && !numericColumns.includes(sortConfig.key)) {
            bValue = bValue.toLowerCase();
        }

        // Handle null values for sorting
        if (aValue === null || aValue === undefined) {
            aValue = numericColumns.includes(sortConfig.key) ? 0 : '';
        }
        if (bValue === null || bValue === undefined) {
            bValue = numericColumns.includes(sortConfig.key) ? 0 : '';
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(sortedUsers.length / usersPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleEdit = (user) => {
        setEditUser(user);
    };

    const handleDelete = (user) => {
        setDeleteUser(user);
    };

    const handleEditSubmit = async (updatedUser) => {
        try {
            if (!updatedUser.user_id) return;

            const response = await fetchPostApi(`/admin/users/${updatedUser.user_id}`, updatedUser);

            if (response.success) {
                const updatedUsers = users.map(u => {
                    if (u.user_id === updatedUser.user_id) {
                        return {
                            ...u,
                            firstName: updatedUser.firstName,
                            lastName: updatedUser.lastName,
                            email: updatedUser.email,
                            role: updatedUser.role
                        };
                    }
                    return u;
                });
                setUsers(updatedUsers);
                setEditUser(null);
            }
        } catch (error) {
            alert('Failed to update user. Please try again.');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteUser) return;

        try {
            const response = await fetchPostApi(`/admin/users/${deleteUser.user_id}`, {}, 'DELETE');

            if (response.success) {
                // Remove user from local state
                setUsers(prevUsers => prevUsers.filter(user => user.user_id !== deleteUser.user_id));
                setDeleteUser(null);
            } else {
                alert('Failed to delete user. Please try again.');
            }
        } catch (error) {
            // Handle specific foreign key constraint error
            if (error.response?.error && error.response.error.includes('foreign key constraint')) {
                alert('Cannot delete this user because they have associated content (news articles, creations, etc.). Please remove their content first or contact an administrator.');
            } else if (error.response?.status === 404) {
                alert('Delete endpoint not found. Please contact an administrator.');
            } else if (error.response?.status === 401 || error.response?.status === 403) {
                alert('You do not have permission to delete users.');
            } else {
                alert('Failed to delete user. Please try again or contact an administrator.');
            }
        }
    };

    const handleRoleToggle = async (user, newRole) => {
        if (user.role === newRole) return;
        try {
            const updatedUser = {
                ...user,
                role: newRole
            };
            const response = await fetchPostApi(`/admin/users/${user.user_id}`, updatedUser);
            if (response.success) {
                setUsers(users.map(u => {
                    if (u.user_id === user.user_id) {
                        return {
                            ...u,
                            role: newRole
                        };
                    }
                    return u;
                }));
            }
        } catch (error) {
            alert('Failed to update user role. Please try again.');
        }
    };

    const handleStatusToggle = async (user, newStatus) => {
        try {
            const updatedUser = {
                ...user,
                accountStatus: newStatus
            };
            const response = await fetchPostApi(`/admin/users/${user.user_id}`, updatedUser);
            if (response.success) {
                setUsers(users.map(u => {
                    if (u.user_id === user.user_id) {
                        return {
                            ...u,
                            accountStatus: newStatus
                        };
                    }
                    return u;
                }));
            }
        } catch (error) {
            alert('Failed to update user status. Please try again.');
        }
    };

    if (!users || users.length === 0) {
        return <div className="hyk-table">No users found.</div>;
    }

    const getSortIcon = (key) => {
        return <Icon alias={'arrow-up-down'} width={16} height={16} color={(sortConfig.key !== key)?"#0A0F1E":"#FF3232"} />;
    };

    // Mobile card view component
    const MobileUserCard = ({ user }) => (
        <div className="mobile-table-card">
            <div className="card-header">
                <div className="card-title">
                    {getUserDisplayName(user)}
                </div>
            </div>
            <div className="card-content">
                <div className="content-row">
                    <span className="label">Email:</span>
                    <span className="value">{user.email}</span>
                </div>
                <div className="content-row">
                    <span className="label">Username:</span>
                    <span className="value">
                        {user.userName ? (
                            <button 
                                onClick={() => navigate(`/admin/users/${user.user_id}`)}
                                className="username"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', color: 'inherit' }}
                            >
                                {user.userName.startsWith('@') ? user.userName : `@${user.userName}`}
                            </button>
                        ) : (
                            <span className="no-username">—</span>
                        )}
                    </span>
                </div>
                <div className="content-row">
                    <span className="label">Invited by:</span>
                    <span className="value">
                        {user.inviter_username ? (
                            <span>
                                {user.inviter_username.startsWith('@') ? user.inviter_username : `@${user.inviter_username}`}
                            </span>
                        ) : user.invited_by_user_id ? (
                            <span>User #{user.invited_by_user_id}</span>
                        ) : (
                            <span>Direct signup</span>
                        )}
                    </span>
                </div>
                <div className="content-row">
                    <span className="label">Role:</span>
                    <span className="value">
                        <div className="role-toggle-container">
                            <button
                                className={`role-toggle ${user.role === 'creator' ? 'creator selected' : 'creator'}`}
                                onClick={() => handleRoleToggle(user, 'creator')}
                            >
                                Creator
                            </button>
                            <button
                                className={`role-toggle ${user.role === 'customer' ? 'user selected' : 'user'}`}
                                onClick={() => handleRoleToggle(user, 'customer')}
                            >
                                User
                            </button>
                        </div>
                    </span>
                </div>
                {isCreator(user) && (
                    <>
                        <div className="content-row">
                            <span className="label"># Creations:</span>
                            <span className="value">
                                <div className="creator-metric-mobile">
                                    <span className="metric-value">{user.creations_count || 0}</span>
                                    {isEligible(user) && (
                                        <span className="eligibility-badge" title="Eligible for 0% cashout fees forever">
                                            ✓
                                        </span>
                                    )}
                                </div>
                            </span>
                        </div>
                        <div className="content-row">
                            <span className="label"># Accepted Invites:</span>
                            <span className="value">
                                <div className="creator-metric-mobile">
                                    <span className="metric-value">{user.accepted_invites_count || 0}</span>
                                    {isEligible(user) && (
                                        <span className="eligibility-badge" title="Eligible for 0% cashout fees forever">
                                            ✓
                                        </span>
                                    )}
                                </div>
                            </span>
                        </div>
                    </>
                )}
                <div className="content-row">
                    <span className="label">Status:</span>
                    <span className="value">
                        <div className="status-toggle-container">
                            <button
                                className={`status-toggle active ${user.accountStatus?.toLowerCase() === 'active' ? 'selected' : ''}`}
                                onClick={() => user.accountStatus?.toLowerCase() !== 'active' && handleStatusToggle(user, 'active')}
                            >
                                Active
                            </button>
                            <button
                                className={`status-toggle inactive ${user.accountStatus?.toLowerCase() === 'inactive' ? 'selected' : ''}`}
                                onClick={() => user.accountStatus?.toLowerCase() !== 'inactive' && handleStatusToggle(user, 'inactive')}
                            >
                                Inactive
                            </button>
                        </div>
                    </span>
                </div>
                <div className="content-row">
                    <span className="label">Created:</span>
                    <span className="value">{moment(user.created_at).format('YYYY-MM-DD')}</span>
                </div>
                <div className="content-row">
                    <span className="label">Email:</span>
                    <span className="value">
                        <span
                            className={`activation-badge ${isEmailVerified(user) ? 'complete' : 'incomplete'}`}
                            title={getEmailVerifiedTooltip(user)}
                        >
                            {isEmailVerified(user) ? '✓' : '✗'}
                        </span>
                    </span>
                </div>
                <div className="content-row">
                    <span className="label">Activation:</span>
                    <span className="value">
                        <div className="activation-indicators">
                            <span
                                className={`activation-badge ${isProfileComplete(user) ? 'complete' : 'incomplete'}`}
                                title={isProfileComplete(user) ? 'Profile completed' : 'Profile incomplete'}
                            >
                                {isProfileComplete(user) ? '✓' : '✗'}
                            </span>
                            <span
                                className={`activation-badge ${hasFirstPublication(user) ? 'complete' : 'incomplete'}`}
                                title={hasFirstPublication(user)
                                    ? `1st publication: ${user.first_post_date ? moment(user.first_post_date).format('YYYY-MM-DD') : 'Yes'}`
                                    : 'No publication yet'}
                            >
                                {hasFirstPublication(user) ? '✓' : '✗'}
                            </span>
                            <span
                                className={`activation-badge ${hasFirstContribution(user) ? 'complete' : 'incomplete'}`}
                                title={hasFirstContribution(user)
                                    ? `1st earning: ${moment(user.first_contribution_date).format('YYYY-MM-DD')}`
                                    : 'No earnings yet'}
                            >
                                {hasFirstContribution(user) ? '✓' : '✗'}
                            </span>
                        </div>
                    </span>
                </div>
            </div>
            <div className="card-actions">
                <button className="edit" onClick={() => handleEdit(user)}>Edit</button>
                <button className="delete" onClick={() => handleDelete(user)}>Delete</button>
            </div>
        </div>
    );

    return (
        <div className="hyk-table">
            <div className="table-header">
                <div className="table-header-left">
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        {searchQuery && (
                            <span className="search-count" style={{right: '40px'}}>
                                {filteredUsers.length} results
                            </span>
                        )}
                        <Icon style={{position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)'}} alias={'search'} width={20} height={20} color="#0A0F1E" />
                    </div>
                    
                    <div className="filter-buttons-group">
                        <div className="filter-group">
                            <button
                                className={`filter-btn ${userTypeFilter === 'all' ? 'active' : ''}`}
                                onClick={() => handleUserTypeFilter('all')}
                            >
                                All
                            </button>
                            <button
                                className={`filter-btn ${userTypeFilter === 'users' ? 'active' : ''}`}
                                onClick={() => handleUserTypeFilter('users')}
                            >
                                Users
                            </button>
                            <button
                                className={`filter-btn ${userTypeFilter === 'creators' ? 'active' : ''}`}
                                onClick={() => handleUserTypeFilter('creators')}
                            >
                                Creators
                            </button>
                        </div>
                        <div className="filter-group">
                            <button
                                className={`filter-btn ${roleStatusFilter === 'admin' ? 'active' : ''}`}
                                onClick={() => handleRoleStatusFilter('admin')}
                            >
                                Admin
                            </button>
                            <button
                                className={`filter-btn ${roleStatusFilter === 'activity' ? 'active' : ''}`}
                                onClick={() => handleRoleStatusFilter('activity')}
                            >
                                Activity
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-header-right">
                    <div className="header-pagination">
                        <button
                            className="pagination-arrow"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            aria-label="Previous page"
                        >
                            <Icon alias="chevron-left" width={16} height={16} color={currentPage === 1 ? "#ccc" : "#0A0F1E"} />
                        </button>
                        <span className="pagination-info">
                            {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, sortedUsers.length)} of {sortedUsers.length}
                        </span>
                        <button
                            className="pagination-arrow"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            aria-label="Next page"
                        >
                            <Icon alias="chevron-right" width={16} height={16} color={currentPage === totalPages ? "#ccc" : "#0A0F1E"} />
                        </button>
                    </div>

                    <div className="header-actions">
                        <button
                            className="header-action-btn"
                            onClick={() => setShowEditTableModal(true)}
                            aria-label="Edit table"
                        >
                            <Icon alias="plus" width={16} height={16} color="#0A0F1E" />
                            <span>Edit table</span>
                        </button>
                        <button
                            className="header-action-btn"
                            onClick={() => {
                                // TODO: Implement export functionality
                            }}
                            aria-label="Export"
                        >
                            <span>Export</span>
                            <Icon alias="chevron-down" width={16} height={16} color="#0A0F1E" />
                        </button>
                    </div>
                </div>
            </div>

            {isMobile ? (
                // Mobile card view
                <div className="mobile-users-list">
                    {currentUsers.map((user) => (
                        <MobileUserCard key={user.user_id} user={user} />
                    ))}
                </div>
            ) : (
                // Desktop table view
                <div className="table-wrapper">
                    <table>
                    <thead>
                        <tr>
                            {columnVisibility.username && (
                                <th onClick={() => handleSort('userName')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Username {getSortIcon('userName')}</div>
                                </th>
                            )}
                         
                            {columnVisibility.email && (
                                <th onClick={() => handleSort('email')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Email {getSortIcon('email')}</div>
                                </th>
                            )}
                            {columnVisibility.invitedBy && (
                                <th onClick={() => handleSort('inviter_username')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Invited by {getSortIcon('inviter_username')}</div>
                                </th>
                            )}
                            {columnVisibility.role && (
                                <th onClick={() => handleSort('role')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Role {getSortIcon('role')}</div>
                                </th>
                            )}
                            {columnVisibility.status && (
                                <th onClick={() => handleSort('accountStatus')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Status {getSortIcon('accountStatus')}</div>
                                </th>
                            )}
                            {columnVisibility.createdAt && (
                                <th onClick={() => handleSort('created_at')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Created At {getSortIcon('created_at')}</div>
                                </th>
                            )}
                            {columnVisibility.emailVerified && (
                                <th onClick={() => handleSort('email_verified')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Verified {getSortIcon('email_verified')}</div>
                                </th>
                            )}
                            {columnVisibility.profileCompleted && (
                                <th onClick={() => handleSort('profile_completed')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Profile {getSortIcon('profile_completed')}</div>
                                </th>
                            )}
                            {columnVisibility.firstPublication && (
                                <th onClick={() => handleSort('has_first_publication')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> 1st Pub {getSortIcon('has_first_publication')}</div>
                                </th>
                            )}
                            {columnVisibility.firstContribution && (
                                <th onClick={() => handleSort('has_first_contribution')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> 1st Contrib {getSortIcon('has_first_contribution')}</div>
                                </th>
                            )}
                            {columnVisibility.earnings && (
                                <th onClick={() => handleSort('earnings')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Earnings {getSortIcon('earnings')}</div>
                                </th>
                            )}
                            {columnVisibility.spendings && (
                                <th onClick={() => handleSort('spendings')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Spendings {getSortIcon('spendings')}</div>
                                </th>
                            )}
                            {columnVisibility.subscribers && (
                                <th onClick={() => handleSort('subscribers')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Subscribers {getSortIcon('subscribers')}</div>
                                </th>
                            )}
                            {columnVisibility.contributors && (
                                <th onClick={() => handleSort('contributors')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Contributors {getSortIcon('contributors')}</div>
                                </th>
                            )}
                            {columnVisibility.contributions && (
                                <th onClick={() => handleSort('contributions')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Contributions {getSortIcon('contributions')}</div>
                                </th>
                            )}
                            {columnVisibility.invitesUsers && (
                                <th onClick={() => handleSort('invites_users')} className="sortable-users">
                                   <div className="d-flex f-row aic jcsb"> Invites users {getSortIcon('invites_users')}</div>
                                </th>
                            )}
                            <th onClick={() => handleSort('creations_count')} className="sortable-users">
                               <div className="d-flex f-row aic jcsb"> # Creations {getSortIcon('creations_count')}</div>
                            </th>
                            <th onClick={() => handleSort('accepted_invites_count')} className="sortable-users">
                               <div className="d-flex f-row aic jcsb"> # Accepted Invites {getSortIcon('accepted_invites_count')}</div>
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentUsers.map((user) => (
                            <tr key={user.user_id}>
                                {columnVisibility.username && (
                                    <td>
                                        {user.userName ? (
                                            <button 
                                                onClick={() => navigate(`/admin/users/${user.user_id}`)}
                                                className="user-link username"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                                            >
                                                {user.userName.startsWith('@') ? user.userName : `@${user.userName}`}
                                            </button>
                                        ) : (
                                            <span className="no-username">—</span>
                                        )}
                                    </td>
                                )}
                                
                                {columnVisibility.email && (
                                    <td>{user.email}</td>
                                )}
                                {columnVisibility.invitedBy && (
                                    <td>
                                        {user.inviter_username ? (
                                            <span>
                                                {user.inviter_username.startsWith('@') ? user.inviter_username : `@${user.inviter_username}`}
                                            </span>
                                        ) : user.invited_by_user_id ? (
                                            <span>User #{user.invited_by_user_id}</span>
                                        ) : (
                                            <span>Direct signup</span>
                                        )}
                                    </td>
                                )}
                                {columnVisibility.role && (
                                    <td>
                                        <div className="role-toggle-container">
                                           { (user.role === 'creator' || user.role==='admin') ? <button
                                                className={`role-toggle user selected`}
                                                onClick={() => handleRoleToggle(user, 'creator')}
                                            >
                                                Creator
                                            </button>
                                            : <button
                                                className={`role-toggle user selected`}
                                                onClick={() => handleRoleToggle(user, 'customer')}
                                            >
                                                User
                                            </button>
                                     } </div>
                                    </td>
                                )}
                                {columnVisibility.status && (
                                    <td>
                                        <div className="status-toggle-container">
                                           {user.accountStatus?.toLowerCase() === 'active' ? <button
                                                className={`status-toggle active ${user.accountStatus?.toLowerCase() === 'active' ? 'selected' : ''}`}
                                                onClick={() => user.accountStatus?.toLowerCase() !== 'active' && handleStatusToggle(user, 'active')}
                                            >
                                                Active
                                            </button>
                                            :
                                            <button
                                                className={`status-toggle inactive inactive`}
                                                onClick={() => user.accountStatus?.toLowerCase() !== 'inactive' && handleStatusToggle(user, 'inactive')}
                                            >
                                                Inactive
                                            </button>}
                                        </div>
                                    </td>
                                )}
                                {columnVisibility.createdAt && (
                                    <td>{moment(user.created_at).format('YYYY-MM-DD')}</td>
                                )}
                                {columnVisibility.emailVerified && (
                                    <td>
                                        <div className="activation-cell">
                                            <span
                                                className={`activation-badge ${isEmailVerified(user) ? 'complete' : 'incomplete'}`}
                                                title={getEmailVerifiedTooltip(user)}
                                            >
                                                {isEmailVerified(user) ? '✓' : '✗'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {columnVisibility.profileCompleted && (
                                    <td>
                                        <div className="activation-cell">
                                            <span
                                                className={`activation-badge ${isProfileComplete(user) ? 'complete' : 'incomplete'}`}
                                                title={isProfileComplete(user) ? 'Profile completed' : 'Profile incomplete'}
                                            >
                                                {isProfileComplete(user) ? '✓' : '✗'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {columnVisibility.firstPublication && (
                                    <td>
                                        <div className="activation-cell">
                                            <span
                                                className={`activation-badge ${hasFirstPublication(user) ? 'complete' : 'incomplete'}`}
                                                title={hasFirstPublication(user)
                                                    ? `1st publication: ${user.first_post_date ? moment(user.first_post_date).format('YYYY-MM-DD') : 'Yes'}`
                                                    : 'No publication yet'}
                                            >
                                                {hasFirstPublication(user) ? '✓' : '✗'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {columnVisibility.firstContribution && (
                                    <td>
                                        <div className="activation-cell">
                                            <span
                                                className={`activation-badge ${hasFirstContribution(user) ? 'complete' : 'incomplete'}`}
                                                title={hasFirstContribution(user)
                                                    ? `1st earning: ${moment(user.first_contribution_date).format('YYYY-MM-DD')}`
                                                    : 'No earnings yet'}
                                            >
                                                {hasFirstContribution(user) ? '✓' : '✗'}
                                            </span>
                                        </div>
                                    </td>
                                )}
                                {columnVisibility.earnings && (
                                    <td>
                                        {user.earnings !== undefined && user.earnings !== null
                                            ? `€${(user.earnings / 100).toFixed(2)}`
                                            : '—'}
                                    </td>
                                )}
                                {columnVisibility.spendings && (
                                    <td>
                                        {user.spendings !== undefined && user.spendings !== null
                                            ? `€${(user.spendings / 100).toFixed(2)}` 
                                            : '—'}
                                    </td>
                                )}
                                {columnVisibility.subscribers && (
                                    <td>{user.subscribers !== undefined && user.subscribers !== null ? user.subscribers : 0}</td>
                                )}
                                {columnVisibility.contributors && (
                                    <td>{user.contributors !== undefined && user.contributors !== null ? user.contributors : 0}</td>
                                )}
                                {columnVisibility.contributions && (
                                    <td>{user.contributions !== undefined && user.contributions !== null ? user.contributions : 0}</td>
                                )}
                                {columnVisibility.invitesUsers && (
                                    <td>{user.invites_users !== undefined && user.invites_users !== null ? user.invites_users : 0}</td>
                                )}

                                <td>
                                    <div className="creator-metric-cell">
                                        {isCreator(user) ? (
                                            <>
                                                <span className="metric-value">{user.creations_count || 0}</span>
                                                {isEligible(user) && (
                                                    <span className="eligibility-badge" title="Eligible for 0% cashout fees forever">
                                                        ✓
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="metric-na">—</span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="creator-metric-cell">
                                        {isCreator(user) ? (
                                            <>
                                                <span className="metric-value">{user.accepted_invites_count || 0}</span>
                                                {isEligible(user) && (
                                                    <span className="eligibility-badge" title="Eligible for 0% cashout fees forever">
                                                        ✓
                                                    </span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="metric-na">—</span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        <button className="edit-btn" onClick={() => handleEdit(user)}>Edit</button>
                                        <button className="delete-btn" onClick={() => handleDelete(user)}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="pagination">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}

            {editUser && (
                <EditUserForm
                    user={editUser}
                    onSave={handleEditSubmit}
                    onClose={() => setEditUser(null)}
                />
            )}

            {deleteUser && (
                <DeleteConfirmation
                    user={deleteUser}
                    onConfirm={handleDeleteConfirm}
                    onClose={() => setDeleteUser(null)}
                />
            )}

            {showEditTableModal && (
                <Modal onClose={() => setShowEditTableModal(false)}>
                    <div className="edit-table-modal">
                        <div className="edit-table-header">
                            <h2>Edit table</h2>
                            <button
                                className="close-btn"
                                onClick={() => setShowEditTableModal(false)}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>

                        <div className="edit-table-content">
                            <div className="edit-table-section">
                                <label className="section-label">Max number of rows</label>
                                <div className="rows-buttons">
                                    {[10, 25, 50, 100].map((num) => (
                                        <button
                                            key={num}
                                            className={`rows-btn ${usersPerPage === num ? 'active' : ''}`}
                                            onClick={() => setUsersPerPage(num)}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="edit-table-section">
                                <label className="section-label">Columns</label>
                                <div className="columns-list">
                                    {[
                                        { key: 'username', label: 'Username' },
                                        { key: 'admin', label: 'Admin' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'invitedBy', label: 'Invited by' },
                                        { key: 'role', label: 'Role' },
                                        { key: 'status', label: 'Status' },
                                        { key: 'createdAt', label: 'Created at' },
                                        { key: 'emailVerified', label: 'Email Verified' },
                                        { key: 'profileCompleted', label: 'Profile Completed' },
                                        { key: 'firstPublication', label: '1st Publication' },
                                        { key: 'firstContribution', label: '1st Contribution' },
                                        { key: 'earnings', label: 'Earnings' },
                                        { key: 'spendings', label: 'Spendings' },
                                        { key: 'subscribers', label: 'Subscribers' },
                                        { key: 'contributors', label: 'Contributors' },
                                        { key: 'contributions', label: 'Contributions' },
                                        { key: 'invitesUsers', label: 'Invites users' }
                                    ].map((column) => (
                                        <label key={column.key} className="column-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={columnVisibility[column.key]}
                                                onChange={(e) => {
                                                    setColumnVisibility(prev => ({
                                                        ...prev,
                                                        [column.key]: e.target.checked
                                                    }));
                                                }}
                                            />
                                            <span className="checkbox-custom"></span>
                                            <span className="checkbox-label">{column.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="edit-table-actions">
                            <button
                                className="cancel-btn"
                                onClick={() => setShowEditTableModal(false)}
                            >
                                CANCEL
                            </button>
                            <button
                                className="save-btn"
                                onClick={() => {
                                    setCurrentPage(1); // Reset to first page when changing rows per page
                                    setShowEditTableModal(false);
                                }}
                            >
                                SAVE CHANGES
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default UserTable;
